import { z, ZodTypeAny } from 'zod';

import { PixSchema } from './pix';

export type RequirementType = z.infer<typeof requirementTypeSchema>;
export type ArtefactPure = z.infer<typeof artefactImageSchema>;
export type Artefact = z.infer<typeof nameToArtefactSchemaMap[keyof typeof nameToArtefactSchemaMap]>;
export type Evidence = z.infer<typeof baseEvidenceSchema> & { artefact?: Artefact | null };
export type Requirement = z.infer<typeof requirementShowSchemaMap[keyof typeof requirementShowSchemaMap]>;
export type RequirementData = Requirement['step']['requirements'][number];
export type RequirementStepsInfo = z.infer<typeof stepsSchema>['steps'];
export type RubricateArtifactSchema = z.infer<typeof artefactRubricateSchema>;

export const requirementTypeSchema = z.union([
  z.literal('initials'),
  z.literal('manuscript'),
  z.literal('handwritten'),
  z.literal('official_document'),
  z.literal('selfie'),
  z.literal('federal_data'),
  z.literal('address_proof'),
  z.literal('pix'),
  z.literal('facial_biometrics'),
  z.literal('liveness'),
  z.literal('icp_brasil'),
  z.literal('email'),
  z.literal('whatsapp'),
  z.literal('sms'),
  z.literal('agree'),
  z.literal('rubricate'),
  z.literal('biometric'),
  z.literal('documentscopy'),
]);

export const stepsSchema = z.object({
  steps: z.object({
    all: z.array(requirementTypeSchema),
    finished: z.array(requirementTypeSchema),
  }),
});

const artefactImageSchema = z.object({
  imageUrl: z.string(),
  filename: z.string(),
});

const artefactFacialBiometricsSchema = z.object({
  documentType: z.string(),
  front: artefactImageSchema.nullable(),
  back: artefactImageSchema.nullable(),
  selfie: artefactImageSchema.nullable(),
});

const artefactDocumentscopySchema = z.object({
  documentType: z.string(),
  front: artefactImageSchema.nullable(),
  back: artefactImageSchema.nullable(),
});

const artefactDigitalCertificateSchema = z.object({ key: z.string() });
const artefactInitialsSchema = z.object({ initials: z.string() });
const artefactRubricateSchema = z.object({
  initials: z.string().nullable(),
  manuscript: z.object({
    filename: z.string().nullable(),
    imageUrl: z.string().nullable(),
  }).nullable(),
});

const nameToArtefactSchemaMap = {
  handwritten: artefactImageSchema.nullable(),
  icp_brasil: artefactDigitalCertificateSchema.nullable(),
  selfie: artefactImageSchema.nullable(),
  address_proof: artefactImageSchema.nullable(),
  liveness: artefactImageSchema.nullable(),
  initials: artefactInitialsSchema.nullable(),

  pix: PixSchema,

  official_document: z.object({
    back: artefactImageSchema,
    front: artefactImageSchema,
  }).nullable(),

  email: z.null(),
  sms: z.null(),
  whatsapp: z.null(),
  facial_biometrics: artefactFacialBiometricsSchema.nullable(),
  agree: z.null(),
  federal_data: z.never(),
  manuscript: z.never(),
  rubricate: artefactRubricateSchema,
  biometric: artefactImageSchema.nullable(),
  documentscopy: artefactDocumentscopySchema.nullable(),
} satisfies Record<RequirementType, ZodTypeAny>;

const baseRequirementSchema = z.object({
  key: z.string(),
  filled: z.boolean(),
  evidenceKey: z.string().nullable(),
});

const rubricateRequirementSchema = z.object({
  auth: z.enum(['initials', 'manuscript']),
  documentKey: z.string(),
  documentName: z.string(),
  rubricatePages: z.string(),
  rubricateConditions: z.object({
    kind: z.enum(['initials', 'manuscript']),
    pages: z.array(
      z.object({
        page: z.string(),
        pageOffset: z.array(z.number()).length(2),
        position: z.array(z.number()).length(2),
      }),
    ).or(z.string().transform((value) => [{ page: value, fromApi: true }])),
  }),
});

const nameToRequirementSchemaMap = {
  handwritten: baseRequirementSchema,
  icp_brasil: baseRequirementSchema,
  selfie: baseRequirementSchema,
  address_proof: baseRequirementSchema,
  liveness: baseRequirementSchema,
  pix: baseRequirementSchema,
  official_document: baseRequirementSchema,
  email: baseRequirementSchema,
  sms: baseRequirementSchema,
  whatsapp: baseRequirementSchema,
  facial_biometrics: baseRequirementSchema,
  agree: baseRequirementSchema.extend({
    role: z.string(),
    documentKey: z.string(),
  }),
  federal_data: baseRequirementSchema,
  initials: baseRequirementSchema.extend({
    documentName: z.string(),
    rubricatePages: z.string(),
  }),
  manuscript: baseRequirementSchema,
  rubricate: baseRequirementSchema.merge(rubricateRequirementSchema),
  biometric: baseRequirementSchema,
  documentscopy: baseRequirementSchema,
} satisfies Record<RequirementType, ZodTypeAny>;

const baseEvidenceSchema = z.object({
  key: z.string(),
  state: z.union([z.literal('done'), z.literal('archived'), z.literal('pending'), z.literal('failed')]),
});

function createRequirementResponseSchema<T extends RequirementType>(name: T) {
  const evidenceSchema = baseEvidenceSchema.extend({ artefact: nameToArtefactSchemaMap[name] });
  const rawRequirementSchema = nameToRequirementSchemaMap[name];
  const requirementSchema = z.object({ auth: z.literal(name) }).merge(rawRequirementSchema);

  return z.object({
    step: z.object({
      requirements: z.array(requirementSchema),
      remainingAttempts: z.number().optional().nullable(),
      evidences: z.array(evidenceSchema),
    }),
  });
}

const requirementShowSchemaMap = {
  handwritten: createRequirementResponseSchema('handwritten'),
  official_document: createRequirementResponseSchema('official_document'),
  icp_brasil: createRequirementResponseSchema('icp_brasil'),
  selfie: createRequirementResponseSchema('selfie'),
  liveness: createRequirementResponseSchema('liveness'),
  email: createRequirementResponseSchema('email'),
  whatsapp: createRequirementResponseSchema('whatsapp'),
  sms: createRequirementResponseSchema('sms'),
  address_proof: createRequirementResponseSchema('address_proof'),
  pix: createRequirementResponseSchema('pix'),
  facial_biometrics: createRequirementResponseSchema('facial_biometrics'),
  agree: createRequirementResponseSchema('agree'),
  federal_data: createRequirementResponseSchema('federal_data'),
  initials: createRequirementResponseSchema('initials'),
  manuscript: createRequirementResponseSchema('manuscript'),
  rubricate: createRequirementResponseSchema('rubricate'),
  biometric: createRequirementResponseSchema('biometric'),
  documentscopy: createRequirementResponseSchema('documentscopy'),
} satisfies Record<RequirementType, ZodTypeAny>;

export function getRequirementShowResponseSchema<T extends RequirementType>(name: T) {
  return requirementShowSchemaMap[name];
}

export const finishRequirementsStatusSchema = z.object({
  documentsCount: z.number(),
  documentsSignedCount: z.number(),
});
